import calculateChoiceSelectionsTotals from './calculateChoiceSelectionsTotals';
import calculateMIAMChoiceSetTotalPrice from './calculateMIAMChoiceSetTotalPrice';
import calculateSubstitutionSelectionsTotals from './calculateSubstitutionSelectionsTotals';

export default function calculateTotalsForPurchase(
  purchase: Purchase,
  choiceOrderingMethod: ChoiceOrderingMethod,
  //eslint-disable-next-line
  staged: boolean = false,
  choiceSetDiscountConfig?: ChoiceSetQuantityDiscountConfig,
): PurchaseTotals {
  const {
    choiceSets,
    choiceSelections,
    substitutionSets,
    substitutionSelections,
    miam,
    quantity,
  } = purchase;
  // recursively total all choice selections
  const choiceSelectionsTotals = calculateChoiceSelectionsTotals(
    choiceSelections,
    choiceSets.filter(s => s.displayType !== 'makeameal'),
    choiceOrderingMethod,
    choiceSetDiscountConfig,
  );
  const miamSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] = [];
  getAllMiamChoiceSets(choiceSets, miamSets);
  const miamTotalPrice = { pointsPrice: 0, pointsAward: 0, moneyPrice: 0 };
  miamSets.forEach(miamSet => {
    const miamQty = miam
      ? (miam[miamSet?.key || ''] || {})['quantity'] || 1
      : 0;
    const _miamTotalPrice = calculateMIAMChoiceSetTotalPrice(
      miamSet,
      miamQty,
      choiceOrderingMethod,
    );

    miamTotalPrice.pointsPrice += _miamTotalPrice.pointsPrice;
    miamTotalPrice.pointsAward += _miamTotalPrice.pointsAward;
    miamTotalPrice.moneyPrice += _miamTotalPrice.moneyPrice;
  });

  // recursively total all substitution selections
  const substitutionSelectionsTotals = calculateSubstitutionSelectionsTotals({
    substitutionSelections: substitutionSelections,
    substitutionSets: substitutionSets,
  });
  const { item: rootItem, sizeId } = purchase;

  // if item is composite get the selected composite variety
  const item =
    (!!rootItem.sizes.length && rootItem.sizes.find(s => s.id === sizeId)) ||
    rootItem;

  const individualMoneyPrice =
    item.baseMoneyPrice +
    choiceSelectionsTotals.moneyPrice +
    substitutionSelectionsTotals.moneyPrice;

  const moneyPrice =
    (individualMoneyPrice + miamTotalPrice.moneyPrice) * quantity;

  const individualPointsPrice =
    item.basePointsPrice +
    choiceSelectionsTotals.pointsPrice +
    substitutionSelectionsTotals.pointsPrice;
  const pointsPrice =
    (individualPointsPrice + miamTotalPrice.pointsPrice) * quantity;

  const individualPointsAward =
    item.basePointsAward +
    choiceSelectionsTotals.pointsAward +
    substitutionSelectionsTotals.pointsAward;
  const pointsAward =
    (individualPointsAward + miamTotalPrice.pointsAward) * quantity;

  return {
    pointsPrice,
    pointsAward,
    moneyPrice,
    moneyDiscount: 0,
    discountedMoneyPrice: moneyPrice,
  };
}

export const getAllMiamChoiceSets = (
  choiceSets: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[],
  result: (ValidatedChoiceSet | ValidatedNestedChoiceSet)[],
): (ValidatedChoiceSet | ValidatedNestedChoiceSet)[] => {
  choiceSets.forEach(choiceSet => {
    if (choiceSet.displayType === 'makeameal') {
      result.push(choiceSet);
    }
    const { choices } = choiceSet;
    if (choices && choices.length > 0) {
      choices.forEach(
        (choice: ChoiceWithQuantity | NestedChoiceWithQuantity) => {
          if (choice && 'choiceSets' in choice) {
            getAllMiamChoiceSets(
              ((choice as NestedChoiceWithQuantity).choiceSets as (
                | ValidatedChoiceSet
                | ValidatedNestedChoiceSet
              )[]) || [],
              result,
            );
          }
        },
      );
    }
  });
  return result;
};
